import { useTranslation } from 'react-i18next';
import { cn } from '@/lib';
import type { DeviceStatus } from '../types';

type StatusLabelStyles<TStatus extends string> = Partial<
  Record<TStatus, string>
>;

interface StatusLabelProps<TStatus extends string = DeviceStatus> {
  status: TStatus;
  translationPrefix?: string;
  styles?: StatusLabelStyles<TStatus>;
  size?: 'sm' | 'md';
  className?: string;
}

const deviceStatusStyles: Record<DeviceStatus, string> = {
  GOOD: 'bg-success/10 text-success border-success/20',
  WARNING: 'bg-warning/10 text-warning border-warning/20',
  ERROR: 'bg-error/10 text-error border-error/20',
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
};

/**
 * @component StatusLabel
 * @description Renders a translated status pill with configurable status colours.
 *
 * @props
 *   status (string, required) - Status key used for styling and translation lookup.
 *   translationPrefix (string, optional, default: devices.status) - i18n key prefix for the label.
 *   styles (Record<string, string>, optional) - Status-specific Tailwind classes.
 *   size (sm | md, optional, default: sm) - Visual size of the label.
 *   className (string, optional) - Extra classes for size or layout adjustments.
 *
 * @changelog
 *   2026-06-26 - Added reusable translation and style inputs for non-device statuses.
 */
export function StatusLabel<TStatus extends string = DeviceStatus>({
  status,
  translationPrefix = 'devices.status',
  styles,
  size = 'sm',
  className,
}: StatusLabelProps<TStatus>) {
  const { t } = useTranslation();

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border font-semibold',
        sizeStyles[size],
        getStatusStyle(status, styles),
        className
      )}
    >
      {t(`${translationPrefix}.${status.toLowerCase()}`)}
    </span>
  );
}

function getStatusStyle<TStatus extends string>(
  status: TStatus,
  styles?: StatusLabelStyles<TStatus>
): string {
  return (
    styles?.[status] ??
    deviceStatusStyles[status as DeviceStatus] ??
    'bg-border text-muted border-border'
  );
}
